// src/pages/Pricing.js
import React from "react";
import { useNavigate } from "react-router-dom";

const Pricing = () => {
  const navigate = useNavigate();

  const cards = [
    {
      name: "Llama 3.2",
      endpoint: "/llama-query",
      badge: "Most Popular",
      bullets: [
        "Great balance of speed & quality",
        "Text generation, Q&A, coding help",
        "Optimized for Indian developers & startups",
      ],
    },
    {
      name: "GPT-OSS 20B",
      endpoint: "/gpt-oss-query",
      badge: "Power User",
      bullets: [
        "Heavier 20B open-source model",
        "Better reasoning for complex tasks",
        "Great for agents, workflows & RAG",
      ],
    },
    {
      name: "DeepSeek R1",
      endpoint: "/deepseek-query",
      badge: "Reasoning First",
      bullets: [
        "Strong chain-of-thought style reasoning",
        "Ideal for analysis & planning tasks",
        "Pairs well with tools / agents",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col">
      {/* Top area */}
      <header className="w-full border-b border-slate-800">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl bg-gradient-to-tr from-sky-400 via-violet-500 to-fuchsia-500" />
            <div className="font-semibold tracking-tight">SlickAPI</div>
          </div>

          <div className="flex gap-3 text-sm">
            <button
              onClick={() => navigate("/")}
              className="px-3 py-1 rounded-full border border-slate-700 hover:bg-slate-800 transition"
            >
              Home
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="px-3 py-1 rounded-full bg-sky-500 hover:bg-sky-400 text-black font-medium transition"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 py-10">
          {/* Page title */}
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-sky-400 mb-3">
              Pricing
            </p>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
              Simple pricing for powerful LLM APIs
            </h1>
            <p className="text-slate-300 max-w-2xl mx-auto text-sm md:text-base">
              SlickAPI is currently in{" "}
              <span className="font-semibold text-sky-400">MVP Early-Access</span>.
              <br />
              <span className="font-semibold text-emerald-400">
                Early adopters will not be charged
              </span>{" "}
              while we benchmark usage and stabilize the platform.
            </p>
          </div>

          {/* Early access banner */}
          <div className="mb-10">
            <div className="rounded-2xl border border-emerald-500/40 bg-gradient-to-r from-emerald-950/60 via-slate-950 to-sky-950/60 p-4 md:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-300 mb-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  MVP Early-Access
                </div>
                <p className="text-sm text-slate-100">
                  <span className="font-semibold text-emerald-300">
                    ₹0 / month for early adopters
                  </span>{" "}
                  – use Llama 3.2, GPT-OSS 20B and DeepSeek-R1 without
                  worrying about billing while we refine limits & pricing.
                </p>
              </div>
              <button
                onClick={() => navigate("/dashboard")}
                className="self-start md:self-auto px-4 py-2 rounded-xl bg-emerald-400 text-slate-950 text-sm font-semibold hover:bg-emerald-300 transition"
              >
                Start using the APIs
              </button>
            </div>
          </div>

          {/* Pricing cards */}
          <div className="grid md:grid-cols-3 gap-5">
            {cards.map((card) => (
              <div
                key={card.name}
                className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 flex flex-col justify-between shadow-[0_0_40px_rgba(15,23,42,0.75)]"
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-lg font-semibold">{card.name}</h2>
                    {card.badge && (
                      <span className="text-[10px] px-2 py-1 rounded-full bg-sky-500/10 text-sky-300 border border-sky-600/60">
                        {card.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mb-3">
                    Endpoint:{" "}
                    <span className="font-mono text-slate-200">
                      {card.endpoint}
                    </span>
                  </p>

                  <div className="mb-4">
                    <div className="text-3xl font-bold">
                      ₹0
                      <span className="text-sm font-normal text-slate-400">
                        {" "}
                        / month (MVP)
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Future pricing will be based on daily request limits per
                      plan.
                    </p>
                  </div>

                  <ul className="space-y-2 mb-4 text-xs text-slate-200">
                    {card.bullets.map((b) => (
                      <li key={b} className="flex gap-2">
                        <span className="mt-[3px] h-1.5 w-1.5 rounded-full bg-sky-400" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => navigate("/dashboard")}
                    className="w-full text-xs font-semibold rounded-lg bg-sky-500 text-black py-2 hover:bg-sky-400 transition"
                  >
                    Use {card.name} from Dashboard
                  </button>
                  {/* <button
                    onClick={() => navigate("/dashboard")}
                    className="w-full text-[11px] rounded-lg border border-slate-700 py-2 text-slate-300 hover:bg-slate-800 transition"
                  >
                    Generate API key & call via REST
                  </button> */}
                </div>
              </div>
            ))}
          </div>

          {/* Future pricing note */}
          <div className="mt-10 text-[11px] text-slate-500 text-center max-w-3xl mx-auto">
            <p>
              <span className="font-semibold text-slate-300">
                Coming soon:
              </span>{" "}
              transparent, per-plan pricing with daily request quotas, team
              accounts and usage analytics. As an early adopter your feedback
              will help shape final pricing — and you’ll get priority discounts
              when we launch paid plans.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Pricing;
